import { startTransition, useEffect, useMemo, useRef, useState } from "react"
import { useMap } from "react-leaflet"

import type { Layer, LayersControlEvent } from "leaflet"

interface UseOverlaysCheckedReturn<K extends string> {
  checked: Record<K, boolean>
  setOverlayRef: Record<K, (layer: Layer | null) => void>
}

/**
 * Live checked state of the overlays in the layers control, so a group can stay
 * empty until its overlay is actually on the map.
 *
 * The map is the only one that knows about a click on the control, and the event
 * it fires identifies the overlay by the label it was registered with. That label
 * is translated and react-leaflet never re-registers it, so match on the layer
 * instance instead - attach `setOverlayRef[key]` to the overlay's group.
 */
export const useOverlaysChecked = <K extends string>(
  keys: readonly K[],
  initialChecked: Record<K, boolean>,
): UseOverlaysCheckedReturn<K> => {
  const map = useMap()
  const [checked, setChecked] = useState(initialChecked)
  const layers = useRef({} as Record<K, Layer | null>)

  const setOverlayRef = useMemo(
    () =>
      Object.fromEntries(
        keys.map((key) => [
          key,
          (layer: Layer | null) => {
            layers.current[key] = layer
          },
        ]),
      ) as UseOverlaysCheckedReturn<K>["setOverlayRef"],
    [keys],
  )

  useEffect(() => {
    const handleChange = (nextChecked: boolean) => (e: LayersControlEvent) => {
      const key = keys.find((k) => layers.current[k] === e.layer)
      if (!key) return
      // Ticking a checkbox is a discrete event, so React would flush the layer's
      // markers before the browser repaints and the control would look stuck.
      // A transition lets the checkbox paint first.
      startTransition(() => setChecked((prev) => ({ ...prev, [key]: nextChecked })))
    }

    const handleAdd = handleChange(true)
    const handleRemove = handleChange(false)

    map.on("overlayadd", handleAdd)
    map.on("overlayremove", handleRemove)

    return () => {
      map.off("overlayadd", handleAdd)
      map.off("overlayremove", handleRemove)
    }
  }, [map, keys])

  return { checked, setOverlayRef }
}
